import { HttpClient } from '@angular/common/http';
import { Injectable, Signal, computed, signal } from '@angular/core';
import { Observable, catchError, of, tap } from 'rxjs';
import { environment } from '../../../environments/environment';
import { LocalStorageService } from '../wrapper/local-storage.service';
import { Banner, DeploymentInfo } from './banner.model';

@Injectable({
  providedIn: 'root',
})
export class BannerService {

  private static readonly BANNERS_URL = 'assets/banners.json';
  private static readonly DISMISSED_STORAGE_KEY = 'af-dismissed-banners';

  private readonly banners = signal<Banner[]>([]);
  private readonly dismissedIds = signal<ReadonlySet<string>>(this.readDismissedIds());

  public readonly deploymentInfo: DeploymentInfo = this.readDeploymentInfo();

  /** Banners that are currently scheduled and not dismissed by this user. */
  public readonly activeBanners: Signal<Banner[]> = computed(() => {
    const now = Date.now();
    const dismissed = this.dismissedIds();
    return this.banners().filter(
      (banner) => !dismissed.has(banner.id) && this.isWithinSchedule(banner, now)
    );
  });

  constructor(
    private readonly http: HttpClient,
    private readonly localStorage: LocalStorageService
  ) {}

  /**
   * Fetches banner definitions and updates the `activeBanners` state.
   * The returned observable is fire-and-forget — subscribe to trigger it;
   * results are read via the `activeBanners` signal, not the emitted value.
   */
  public load(): Observable<Banner[]> {
    return this.http
      .get<Banner[]>(`${BannerService.BANNERS_URL}?t=${Date.now()}`)
      .pipe(
        tap((banners) => this.setBanners(banners)),
        catchError(() => of<Banner[]>([]))
      );
  }

  /** Hides a banner for this user and remembers the choice across sessions. */
  public dismiss(id: string): void {
    this.setDismissedIds(new Set(this.dismissedIds()).add(id));
  }

  private setBanners(banners: unknown): void {
    const normalized = Array.isArray(banners) ? banners : [];
    this.banners.set(normalized);
    this.pruneDismissedIds(normalized);
  }

  private isWithinSchedule(banner: Banner, now: number): boolean {
    const notYetStarted = banner.startsAt && now < Date.parse(banner.startsAt);
    const alreadyEnded = banner.endsAt && now > Date.parse(banner.endsAt);
    return !notYetStarted && !alreadyEnded;
  }

  /**
   * Removes dismissed ids from storage that no longer have a matching banner in
   * `banners.json` or whose banner is no longer within its schedule.
   */
  private pruneDismissedIds(banners: Banner[]): void {
    const now = Date.now();
    const scheduledIds = new Set(
      banners
        .filter((banner) => this.isWithinSchedule(banner, now))
        .map((banner) => banner.id)
    );
    const retained = new Set(
      [...this.dismissedIds()].filter((id) => scheduledIds.has(id))
    );
    this.setDismissedIds(retained);
  }

  /** Single place that updates the dismissed-ids signal and persists it. */
  private setDismissedIds(ids: ReadonlySet<string>): void {
    this.dismissedIds.set(ids);
    this.localStorage.setItem(
      BannerService.DISMISSED_STORAGE_KEY,
      JSON.stringify([...ids])
    );
  }

  private readDismissedIds(): ReadonlySet<string> {
    const raw = this.localStorage.getItem(BannerService.DISMISSED_STORAGE_KEY);
    if (!raw) {
      return new Set<string>();
    }
    try {
      const ids: unknown = JSON.parse(raw);
      return new Set<string>(Array.isArray(ids) ? ids : []);
    } catch {
      return new Set<string>();
    }
  }
  private readDeploymentInfo(): DeploymentInfo {
    return environment.deploymentInfo;
  }
}
